import * as React from "react";
import { cn } from "@/lib/utils";
import { ExampleKey, FORM_TYPE_LABELS, ROUTE_EXAMPLES } from "../useFormRouteBuilder";

const EXAMPLE_COPY: Record<ExampleKey, { title: string; description: string }> = {
  mid_loan: {
    title: "Check in mid-loan",
    description: "Ask how it's going at a milestone like clear-to-close, while there's still time to fix things.",
  },
  closing: {
    title: "Ask at closing",
    description: "Ask every borrower for a review right after closing, from the Player who closed their loan.",
  },
  past_borrowers: {
    title: "Catch up with past borrowers",
    description: "One email to every funded borrower who hasn't left a review yet.",
  },
  lead_gen: {
    title: "Collect leads on your website",
    description: "Add a lead form to each Player's bio page, and every lead goes to that Player.",
  },
};

interface Props {
  selected: ExampleKey | null;
  onPick: (example: ExampleKey) => void;
}

export function RouteExamples({ selected, onPick }: Props) {
  return (
    <div className="flex flex-col gap-2" data-testid="route-examples">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Start from an example</span>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ROUTE_EXAMPLES.map(example => {
          const { title, description } = EXAMPLE_COPY[example.key];
          const pressed = example.key === selected;
          return (
            <button
              key={example.key}
              type="button"
              aria-pressed={pressed}
              onClick={() => onPick(example.key)}
              className={cn(
                "flex flex-col gap-1 rounded-xl border px-3.5 py-3 text-left transition-colors cursor-pointer",
                pressed
                  ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                  : "border-slate-200 bg-white hover:bg-slate-50"
              )}
            >
              <span className="text-sm font-semibold text-slate-800">{title}</span>
              <span className="text-xs text-slate-500 leading-relaxed">{description}</span>
              <span className="mt-auto pt-1 text-[11px] font-medium text-slate-400">
                {example.channel === "website" ? "Website" : "Email"} ·{" "}
                {example.formType ? FORM_TYPE_LABELS[example.formType] : "Your lead form"}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-500">Or build your own below.</p>
    </div>
  );
}
